/**
 * Chart.js setup and configuration
 *
 * This file centralizes all Chart.js component registrations and provides
 * common chart configurations for the application.
 *
 * Only imports the components that are actually used in the app:
 * - Line charts (MentionsProgressCard)
 * - Radar charts (VisibilityScore)
 *
 * This enables optimal tree shaking and reduces bundle size.
 */

import {
  // Elements for both chart types
  CategoryScale,
  // Essential Chart.js core
  Chart as ChartJS,
  Title as ChartTitle,
  Filler,
  Legend,
  LinearScale,
  // Chart types actually used
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
  // Types
  type ScriptableContext,
} from 'chart.js';

// Register ONLY the Chart.js components that are actually used in the app
// This provides optimal tree shaking and minimal bundle size
ChartJS.register(
  // Scales
  CategoryScale,
  LinearScale,
  RadialLinearScale, // Required for radar charts

  // Elements
  LineElement, // Required for line charts
  PointElement, // Required for both line and radar charts
  Filler, // For gradient fills

  // Plugins
  ChartTitle,
  Tooltip,
  Legend,
);

// Common chart options that can be extended by individual components
// NOTE: Do NOT annotate with ChartOptions<...> here.
// A generic ChartOptions annotation widens internal scriptable context types
// (e.g. the global 'color' option) to keyof ChartTypeRegistry, which then
// becomes incompatible when spreading into ChartOptions<'line'> or
// ChartOptions<'radar'> in component code. Leaving this untyped preserves the
// narrow literal property types and avoids TS2322 errors when composing.
export const baseChartOptions = {
  elements: {
    line: {
      backgroundColor: 'rgba(166, 30, 105, 0.2)',
      borderColor: 'rgba(166, 30, 105, 1)',
      borderWidth: 4,
    },
    point: {
      backgroundColor: 'rgba(166, 30, 105, 1)',
      borderColor: 'rgba(255,255,255, .9)',
      borderWidth: 2,
      radius: 5,
      hoverRadius: 6,
      hoverBorderWidth: 3,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  interaction: {
    mode: 'nearest' as const,
    axis: 'x' as const,
    intersect: false,
  },
} as const;

// Yoast brand colors for consistent chart theming
export const chartColors = {
  primary: 'rgba(166, 30, 105, 1)', // Yoast pink
  primaryLight: 'rgba(166, 30, 105, 0.2)',
  secondary: 'rgb(99, 102, 241)', // Indigo
  secondaryLight: 'rgba(99, 102, 241, 0.1)',
  success: 'rgb(34, 197, 94)', // Green
  warning: 'rgb(251, 191, 36)', // Amber
  error: 'rgb(239, 68, 68)', // Red
  neutral: 'rgb(156, 163, 175)', // Gray
} as const;

export { ChartJS };

// Chart gradient functions - memoized for performance
/**
 * Creates a linear gradient for line charts from purple at top to white at bottom
 */
export const createLineChartGradient = (ctx: ScriptableContext<'line'>) => {
  const canvasCtx = ctx.chart.ctx;
  const chartArea = ctx.chart.chartArea;

  if (!chartArea) {
    return 'rgba(166, 30, 105, 0.2)';
  }

  const gradient = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  gradient.addColorStop(0, 'rgba(166, 30, 105, 0.6)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0.1)');
  return gradient;
};

/**
 * Creates a radial gradient for radar charts with white center and purple edges
 */
export const createRadarChartGradient = (ctx: ScriptableContext<'radar'>) => {
  const canvasCtx = ctx.chart.ctx;
  const chartArea = ctx.chart.chartArea;

  if (!chartArea) {
    return 'rgba(166, 30, 105, 0.1)';
  }

  const centerX = (chartArea.left + chartArea.right) / 2;
  const centerY = (chartArea.top + chartArea.bottom) / 2;
  const radius = Math.min(
    (chartArea.right - chartArea.left) / 2,
    (chartArea.bottom - chartArea.top) / 2,
  );

  const gradient = canvasCtx.createRadialGradient(
    centerX,
    centerY,
    0,
    centerX,
    centerY,
    radius,
  );
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.4)');
  gradient.addColorStop(1, 'rgba(166, 30, 105, 0.2)');
  return gradient;
};
